import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Brand } from '../../Services/brandService';

interface BrandFilterDropdownProps {
  brands: Brand[];
  selectedBrand: string; // brand id or '*'
  onSelect: (brandId: string) => void;
}

/**
 * Compact, searchable brand selector.
 *
 * Replaces the previously ever-growing vertical list of brand buttons in the
 * shop filter. Keeps the filter panel a fixed height no matter how many brands
 * exist, with a type-to-filter search for quick access.
 */
const BrandFilterDropdown: React.FC<BrandFilterDropdownProps> = ({ brands, selectedBrand, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedName = useMemo(() => {
    if (selectedBrand === '*') return 'All Brands';
    return brands.find(b => b.id === selectedBrand)?.name ?? 'All Brands';
  }, [selectedBrand, brands]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return brands;
    return brands.filter(b => b.name.toLowerCase().includes(s));
  }, [search, brands]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const choose = (id: string) => {
    onSelect(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="brand-dd" ref={rootRef}>
      <style>{`
        .brand-dd { position: relative; width: 100%; max-width: 260px; }
        .brand-dd__trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 10px 14px;
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .brand-dd__trigger:hover { border-color: #717fe0; }
        .brand-dd__trigger--open { border-color: #717fe0; box-shadow: 0 0 0 3px rgba(113,127,224,0.12); }
        .brand-dd__trigger-label {
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .brand-dd__caret { transition: transform 0.2s ease; flex-shrink: 0; color: #888; }
        .brand-dd__caret--open { transform: rotate(180deg); }
        .brand-dd__panel {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          z-index: 30;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 10px;
          box-shadow: 0 12px 32px rgba(0,0,0,0.14);
          overflow: hidden;
        }
        .brand-dd__search-wrap { padding: 10px; border-bottom: 1px solid #f0f0f0; }
        .brand-dd__search {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 7px;
          font-size: 13px;
          outline: none;
        }
        .brand-dd__search:focus { border-color: #717fe0; }
        .brand-dd__list {
          list-style: none;
          margin: 0;
          padding: 6px;
          max-height: 240px;
          overflow-y: auto;
        }
        .brand-dd__item {
          width: 100%;
          text-align: left;
          padding: 9px 12px;
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13.5px;
          color: #444;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .brand-dd__item:hover { background: #f5f6ff; color: #717fe0; }
        .brand-dd__item--active { background: #717fe0; color: #fff; font-weight: 600; }
        .brand-dd__item--active:hover { background: #5a6fd6; color: #fff; }
        .brand-dd__empty { padding: 16px; text-align: center; color: #999; font-size: 13px; }
      `}</style>

      <button
        type="button"
        className={`brand-dd__trigger ${open ? 'brand-dd__trigger--open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="brand-dd__trigger-label">{selectedName}</span>
        <svg className={`brand-dd__caret ${open ? 'brand-dd__caret--open' : ''}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="brand-dd__panel">
          <div className="brand-dd__search-wrap">
            <input
              className="brand-dd__search"
              type="text"
              placeholder="Search brands…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <ul className="brand-dd__list">
            <li>
              <button
                type="button"
                className={`brand-dd__item ${selectedBrand === '*' ? 'brand-dd__item--active' : ''}`}
                onClick={() => choose('*')}
              >
                All Brands
              </button>
            </li>
            {filtered.map(brand => (
              <li key={brand.id}>
                <button
                  type="button"
                  className={`brand-dd__item ${selectedBrand === brand.id ? 'brand-dd__item--active' : ''}`}
                  onClick={() => choose(brand.id!)}
                >
                  {brand.name}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="brand-dd__empty">No brands match “{search}”</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BrandFilterDropdown;
